import {PayloadAction, createSelector, createSlice} from '@reduxjs/toolkit';
import omit from 'lodash/omit';
import {revisionStringToNumber} from 'pikaparam';

import {Constants} from '../../../constants';
import {bleManager} from '../../ble2/v2/BleManager';
import {IPeakDevice} from '../../ble2/v2/PeakDevice/IPeakDevice';
import {Device, MaybeAggregatedHeatProfile} from '../../types';
import {BleStore, LatestOta, Store} from '../types';

type UpdateDevice = Pick<Device, 'id'> & Partial<Omit<Device, 'id'>>;

const bleSlice = createSlice({
  name: 'ble',
  initialState: {
    devices: [],
  } as BleStore,
  reducers: {
    setConnectedDeviceId(state, action: PayloadAction<string | undefined>) {
      const deviceId = action.payload;

      state.connectedDevice = deviceId
        ? {id: deviceId, connectedAt: Date.now()}
        : undefined;

      if (!deviceId) return;

      state.currentDeviceId = deviceId;

      const index = state.devices.findIndex(d => d.id === deviceId);

      if (index >= 0) {
        state.devices[index] = {
          ...state.devices[index],
          firmwareUpdate: undefined,
        };
      }

      // Reorder devices so the most recently connected device will be the first one in the list
      state.devices = [
        ...state.devices.filter(d => d.id === deviceId),
        ...state.devices.filter(d => d.id !== deviceId),
      ];
    },
    upsertDevice(state, action: PayloadAction<Device>) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) {
        state.devices.push(action.payload);
        return;
      }

      state.devices[index] = {
        ...state.devices[index],
        ...action.payload,
        settings: {
          ...state.devices[index].settings,
          ...action.payload.settings,
        },
        appSettings: {
          ...state.devices[index].appSettings,
          ...action.payload.appSettings,
        },
      };
    },
    updateDevice(state, action: PayloadAction<UpdateDevice>) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      state.devices[index] = {
        ...state.devices[index],
        ...action.payload,
        settings: {
          ...state.devices[index].settings,
          ...action.payload.settings,
        },
        appSettings: {
          ...state.devices[index].appSettings,
          ...action.payload.appSettings,
        },
      };
    },
    updateDeviceAppSettings(
      state,
      action: PayloadAction<Pick<Device, 'id' | 'appSettings'>>,
    ) {
      const ind = state.devices.findIndex(d => d.id === action.payload.id);

      if (ind !== -1) {
        state.devices[ind] = {
          ...state.devices[ind],
          appSettings: {
            ...state.devices[ind].appSettings,
            ...omit(action.payload, 'id').appSettings,
            cleaningReminderLastUpdated:
              action.payload.appSettings?.cleaningReminderLastUpdated ??
              Date.now(),
          },
        };
      }
    },
    updateDeviceHeatProfile(
      state,
      action: PayloadAction<{
        id: string;
        heatProfile: MaybeAggregatedHeatProfile;
      }>,
    ) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      const heatProfiles = state.devices[index].heatProfiles.map(item =>
        item.order === action.payload.heatProfile.order
          ? action.payload.heatProfile
          : item,
      );

      state.devices[index] = {
        ...state.devices[index],
        heatProfiles,
      };
    },
    updateDeviceHeatProfiles(
      state,
      action: PayloadAction<{
        id: string;
        heatProfiles: MaybeAggregatedHeatProfile[];
      }>,
    ) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      const heatProfiles = state.devices[index].heatProfiles.map(item => {
        const found = action.payload.heatProfiles.find(
          hp => hp.order === item.order,
        );

        if (!found) return item;

        return found;
      });

      state.devices[index] = {
        ...state.devices[index],
        heatProfiles,
      };
    },
    updateDeviceSettings(state, action: PayloadAction<Partial<Device>>) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      state.devices[index].settings = {
        ...state.devices[index].settings,
        ...action.payload.settings,
      };
    },

    addDeviceLogs(
      state,
      action: PayloadAction<Required<Pick<Device, 'id' | 'logs'>>>,
    ) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      const logs = state.devices[index].logs ?? [];
      const indices = new Set(logs.map(l => l.offset));

      state.devices[index] = {
        ...state.devices[index],
        logs: logs.concat(
          action.payload.logs.filter(l => !indices.has(l.offset)),
        ),
      };
    },
    removeDeviceLogs(
      state,
      action: PayloadAction<Required<Pick<Device, 'id' | 'logs'>>>,
    ) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      const logs = state.devices[index].logs ?? [];
      const indices = new Set(action.payload.logs.map(l => l.offset));

      state.devices[index] = {
        ...state.devices[index],
        logs: logs.filter(l => !indices.has(l.offset)),
      };
    },
    removeBleDevice(state, action: PayloadAction<string>) {
      state.devices = state.devices.filter(d => d.id !== action.payload);

      if (action.payload !== state.currentDeviceId) return;

      state.currentDeviceId = state.devices[0]?.id;
    },
    setOta(state, action: PayloadAction<LatestOta | undefined>) {
      state.ota = action.payload;
    },
    initiateFirmwareUpdate(
      state,
      action: PayloadAction<Pick<Device, 'id' | 'firmwareUpdate'>>,
    ) {
      const index = state.devices.findIndex(d => d.id === action.payload.id);

      if (index < 0) return;

      state.devices[index] = {
        ...state.devices[index],
        firmwareUpdate: action.payload.firmwareUpdate,
      };
    },
    finishFirmwareUpdate(state) {
      state.devices = state.devices.map(
        ({firmwareUpdate, ...device}) => device,
      );
    },
  },
});

export const currentDeviceIdSelector = (state: Store) =>
  state.ble.currentDeviceId;

export const currentDeviceNameSelector = (state: Store) =>
  currentDeviceSelector(state)?.name;

export const currentDeviceSerialNumberSelector = (state: Store) =>
  currentDeviceSelector(state)?.serialNumberString;

export const currentDeviceModelNumberSelector = (state: Store) =>
  currentDeviceSelector(state)?.modelNumberString;

export const currentDeviceFirmwareUpdateSelector = (state: Store) => {
  const device = currentDeviceSelector(state);

  return device?.firmwareUpdate;
};

export const currentDeviceFirmwarePendingSelector = (state: Store) => {
  const device = currentDeviceSelector(state);

  if (!device?.softwareRevisionString) return false;

  const firmwareUpdate = currentDeviceFirmwareUpdateSelector(state);

  return (
    !!firmwareUpdate &&
    revisionStringToNumber(firmwareUpdate.toVersion) >
      revisionStringToNumber(device.softwareRevisionString)
  );
};

export const currentDeviceSelector = (state: Store) => {
  const id = currentDeviceIdSelector(state);

  if (!id) return;

  return state.ble.devices.find(device => device.id === id);
};

export const connectedPeakSelector = (
  state: Store,
): IPeakDevice | undefined => {
  if (
    state.ble.connectedDevice?.id !== bleManager.peak?.peripheralId ||
    currentDeviceIdSelector(state) !== bleManager.peak?.peripheralId
  )
    return;

  return bleManager.peak;
};

export const devicesSelector = (state: Store) => state.ble.devices;

export const currentDeviceSettingsSelector = (state: Store) =>
  currentDeviceSelector(state)?.settings;

export const currentDeviceStateSelector = (state: Store) =>
  currentDeviceSelector(state)?.state;

export const currentDeviceLogsSelector = (state: Store) =>
  currentDeviceSelector(state)?.logs;

export const currentDeviceSWRevisionSelector = (state: Store) =>
  currentDeviceSelector(state)?.softwareRevisionString ?? '';

export const isOtaAvailableSelector = (state: Store) => {
  const device = currentDeviceSelector(state);

  if (!device?.softwareRevisionString || !state.ble.ota?.version) return false;

  return (
    revisionStringToNumber(state.ble.ota.version) >
    revisionStringToNumber(device.softwareRevisionString)
  );
};

export const currentDeviceHeatProfilesSelector = createSelector(
  currentDeviceSelector,
  currentDevice => {
    if (!currentDevice) return [];

    return currentDevice?.heatProfiles ?? [];
  },
);

export const currentDeviceSelectedHeatProfileSelector = createSelector(
  currentDeviceSelector,
  currentDevice => {
    if (!currentDevice) return;

    if (currentDevice.selectedHeatCycle === Constants.TEMP_HEAT_PROFILE_INDEX)
      return currentDevice.tempHeatProfile;

    return currentDevice.heatProfiles[currentDevice.selectedHeatCycle ?? 0];
  },
);

export const currentDeviceTempHeatProfileSelector = createSelector(
  currentDeviceSelector,
  currentDevice => {
    if (!currentDevice) return;

    return currentDevice.tempHeatProfile;
  },
);

export const {
  setConnectedDeviceId,
  upsertDevice,
  updateDevice,
  updateDeviceAppSettings,
  updateDeviceSettings,
  updateDeviceHeatProfile,
  updateDeviceHeatProfiles,
  addDeviceLogs,
  removeDeviceLogs,
  removeBleDevice,
  setOta,
  initiateFirmwareUpdate,
  finishFirmwareUpdate,
} = bleSlice.actions;

export const bleReducer = bleSlice.reducer;
